import React from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import { Glyphicon, Accordion, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { Panel } from 'axway-react-ui';
import { DragSource } from 'react-dnd';
import cn from 'classnames';

/**
 * A panel for containing drawers of tools.
 */
class ToolPanel extends React.Component {
	render() {
		const { children, empty } = this.props;
		if (React.Children.count(children)) {
			return (
				<Accordion id="tool-panel" className="toolpanel">
					{children}
				</Accordion>
			);
		} else {
			return empty;
		}
	}
}

ToolPanel.defaultProps = {
	empty: (<div className="toolpanel" />)
};

/**
 * A collapsible section in the panel that contains the tools.
 */
class Drawer extends React.Component {
	capitalizeFirstLetter(string) {
		return string.charAt(0).toUpperCase() + string.slice(1);
	}

	render() {
		// expanded is already passed as a special prop from the Accordion to children to allow it
		// to control their expanded state. We can't use it for referring to the horizontal
		// expanded state of the tool panel
		const {
			category,
			items,
			content,
			draggable,
			toolPanelExpanded
		} = this.props;
		const expanded = toolPanelExpanded;
		let tools = content;
		if (items.length) {
			tools = items.map(item => (
				<DraggableTool
					expanded={expanded}
					item={item}
					key={`${item.name || ''}${item.key}`}
					draggable={draggable}
				/>
			)).sort((a, b) => a.key.localeCompare(b.key));
		}
		const headerTitle = <div>{category}</div>;
		const headerElement = expanded
			? category : statusTooltip(headerTitle, this.capitalizeFirstLetter(category));

		return (
			<Panel
				independent
				hasIcon={expanded}
				collapsible
				defaultExpanded
				header={headerElement}
				className="drawer"
			>
				{tools}
			</Panel>
		);
	}
}

function statusTooltip(content, tooltipText) {
	const tooltip = (
		<Tooltip id="tooltip">{tooltipText}</Tooltip>
	);
	return (
		<OverlayTrigger key={tooltipText} tabIndex={0} placement="right" overlay={tooltip}>
			{content}
		</OverlayTrigger>
	);
}

Drawer.propTypes = {
	toolPanelExpanded: PropTypes.bool.isRequired,
	category: PropTypes.string.isRequired,
	/**
	 * A list of tool items for the drawer.  Mutually exclusive with
	 * `content`.
	 */
	items: PropTypes.arrayOf(
		PropTypes.shape({
			name: PropTypes.string.isRequired,
			key: PropTypes.string.isRequired,
			type: PropTypes.string,
			icon: PropTypes.string
		})
	),
	/**
	 * A custom tool item for the drawer.  Mutually exclusive with `items`.
	 */
	content: PropTypes.node
};

Drawer.defaultProps = {
	items: [],
	content: null
};

const toolSource = {
	beginDrag(props, monitor, component) {
		// Save off the height/width of the tool component.  The drop operation works
		// off the upper-left point, but we will want to center.
		const rect = ReactDOM.findDOMNode(component).getBoundingClientRect();
		return {
			item: props.item,
			width: rect.width,
			height: rect.height
		};
	},
	canDrag(props) {
		// handling undefined prop by defaultProp somehow messes up with isDragging, hence did this.
		return props.draggable === undefined ? true : props.draggable;
	}
};

function collect(connect, monitor) {
	return {
		connectDragSource: connect.dragSource(),
		connectDragPreview: connect.dragPreview(),
		isDragging: monitor.isDragging()
	};
}

/**
 * Components contained in the drawer of the toolchest.
 */
class Tool extends React.Component {
	constructor(props) {
		super(props);
		// React DnD isDragging doesn't change to true until the element is moved,
		// to give feedback on mouse down we maintain state here.
		this.state = {
			grabbing: false
		};
		this.startGrab = this.startGrab.bind(this);
		this.endGrab = this.endGrab.bind(this);
		this.grabEvents = {
			onMouseDown: this.startGrab,
			onMouseUp: this.endGrab,
			onMouseLeave: this.endGrab
		};
	}

	startGrab() {
		this.setState({ grabbing: true });
	}

	endGrab() {
		if (this.state.grabbing) {
			this.setState({ grabbing: false });
		}
	}

	render() {
		const { item, connectDragSource, expanded } = this.props;
		let icon;
		if (item.icon) {
			if (item.icon.match(/^data:image\/(?:bmp|jpeg|png|gif|tiff|svg\+xml);base64,.*/)) {
				icon = (
					<img src={item.icon} alt="" />
				);
			} else {
				icon = (
					<Glyphicon glyph={` gg ${item.icon}`} />
				);
			}
		}

		const className = expanded
			? cn('tool', { dragging: this.state.grabbing })
			: cn('collapsed-tool-panel', 'tool', { dragging: this.state.grabbing });
		const content = connectDragSource(
			<div id={`${item.key}`} tabIndex={0} className={className} {...this.grabEvents}>
				<div className="icon">{icon}</div>
				{expanded ? <div className="title" title={item.name}>{item.name}</div> : null}
			</div>
		);
		const tooltip = expanded ? content : statusTooltip(content, item.name);

		return tooltip;
	}
}

Tool.propTypes = {
	item: PropTypes.shape({
		name: PropTypes.string.isRequired,
		key: PropTypes.string.isRequired,
		type: PropTypes.string,
		icon: PropTypes.string
	}).isRequired,

	/**
	 * Swith to enable/disable draggability of the nodehandlers. Enabled by default
	 */
	draggable: PropTypes.bool,

	/**
	 * Injected by react-dnd
	 */
	isDragging: PropTypes.bool.isRequired,
	/**
	 * Injected by react-dnd
	 */
	connectDragSource: PropTypes.func.isRequired
};

// Wrap the Tool to make draggable
const DraggableTool = DragSource('tool', toolSource, collect)(Tool);
ToolPanel.Drawer = Drawer;
ToolPanel.Tool = DraggableTool;

export default ToolPanel;
